import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  ShoppingBag,
  User,
  LogOut,
  LayoutDashboard,
  ClipboardList,
  CalendarClock,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { useCartStore } from "@/store/cart";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getSchedule } from "@/api/settings";
import NotificationsBell from "@/components/layout/NotificationsBell";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();
  const cartCount = useCartStore((s) => s.count());
  const navigate = useNavigate();

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleText, setScheduleText] = useState("");
  const [scheduleImage, setScheduleImage] = useState<string | null>(null);

  const loadSchedule = useCallback(async () => {
    try {
      const data = await getSchedule();
      setScheduleText(data.schedule_text);
      setScheduleImage(data.schedule_image_url);
    } catch { /* ignore */ }
  }, []);

  const handleOpenSchedule = () => {
    void loadSchedule();
    setScheduleOpen(true);
    setMobileOpen(false);
  };

  const isAdmin = user?.role === "admin";
  const isSeller = user?.role === "seller";
  const hasPanel = isAdmin || isSeller;

  const handleLogout = () => {
    logout();
    navigate("/");
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      <div className="container-page flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-bold text-xl">
          <ShoppingBag className="h-6 w-6 text-primary" />
          <span>Katty Shop</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          {isAuthenticated && (
            <Link
              to="/catalog"
              className="text-sm font-medium text-text-secondary hover:text-text transition-colors"
            >
              Каталог
            </Link>
          )}

          {isAuthenticated && (
            <button
              onClick={handleOpenSchedule}
              className="text-sm font-medium text-text-secondary hover:text-text transition-colors flex items-center gap-1"
              data-testid="schedule-btn"
            >
              <CalendarClock className="h-4 w-4" />
              График
            </button>
          )}

          {hasPanel && (
            <Link
              to={isAdmin ? "/admin" : "/seller"}
              className="text-sm font-medium text-text-secondary hover:text-text transition-colors flex items-center gap-1"
            >
              <LayoutDashboard className="h-4 w-4" />
              {isAdmin ? "Админка" : "Панель"}
            </Link>
          )}

          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <Link
                to="/orders"
                className="text-sm font-medium text-text-secondary hover:text-text transition-colors flex items-center gap-1"
              >
                <ClipboardList className="h-4 w-4" />
                Мои заказы
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="sm" className="gap-1.5">
                  <User className="h-4 w-4" />
                  {user?.full_name || user?.email}
                </Button>
              </Link>
              <a
                href="https://proxy4all.ru"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-text-secondary hover:text-[#229ED9] transition-colors flex items-center gap-1"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                Telegram
              </a>
              <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  Войти
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm">Регистрация</Button>
              </Link>
            </div>
          )}

          {isAuthenticated && (
            <Link
              to="/cart"
              className="relative flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium text-text-secondary hover:text-text"
              aria-label="Корзина"
            >
              <ShoppingBag className="h-5 w-5" />
              <span>Корзина</span>
              {cartCount > 0 && (
                <span className="absolute -right-2 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                  {cartCount}
                </span>
              )}
            </Link>
          )}

          {isAuthenticated && <NotificationsBell />}
        </nav>

        {/* Mobile: cart + hamburger (only for authenticated users) */}
        {isAuthenticated && (
          <div className="flex md:hidden items-center gap-1">
            <Link
              to="/cart"
              className="relative flex items-center gap-1 p-2 rounded-lg text-sm font-medium text-text-secondary"
              aria-label="Корзина"
            >
              <ShoppingBag className="h-5 w-5" />
              <span className="text-xs">Корзина</span>
              {cartCount > 0 && (
                <span className="absolute right-0 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                  {cartCount}
                </span>
              )}
            </Link>
            <NotificationsBell />
            <button
              className="p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        )}
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-surface animate-fade-in">
          <nav className="container-page py-4 flex flex-col gap-3">
            {isAuthenticated && (
              <>
                <Link
                  to="/catalog"
                  className="text-sm font-medium py-2"
                  onClick={() => setMobileOpen(false)}
                >
                  Каталог
                </Link>
                <button
                  className="text-sm font-medium py-2 text-left flex items-center gap-2"
                  onClick={handleOpenSchedule}
                >
                  <CalendarClock className="h-4 w-4" />
                  График выдачи
                </button>
                <Link
                  to="/cart"
                  className="text-sm font-medium py-2 flex items-center gap-2"
                  onClick={() => setMobileOpen(false)}
                >
                  <ShoppingBag className="h-4 w-4" />
                  Корзина
                  {cartCount > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                      {cartCount}
                    </span>
                  )}
                </Link>
              </>
            )}
            {hasPanel && (
              <Link
                to={isAdmin ? "/admin" : "/seller"}
                className="text-sm font-medium py-2 flex items-center gap-2"
                onClick={() => setMobileOpen(false)}
              >
                <LayoutDashboard className="h-4 w-4" />
                {isAdmin ? "Админка" : "Панель"}
              </Link>
            )}
            {isAuthenticated ? (
              <>
                <Link
                  to="/orders"
                  className="text-sm font-medium py-2 flex items-center gap-2"
                  onClick={() => setMobileOpen(false)}
                >
                  <ClipboardList className="h-4 w-4" />
                  Мои заказы
                </Link>
                <a
                  href="https://proxy4all.ru"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium py-2 flex items-center gap-1 text-text-secondary hover:text-[#229ED9] transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                  Telegram
                </a>
                <Link
                  to="/profile"
                  className="text-sm font-medium py-2"
                  onClick={() => setMobileOpen(false)}
                >
                  Профиль
                </Link>
                <button
                  className="text-sm font-medium py-2 text-left text-danger"
                  onClick={handleLogout}
                >
                  Выйти
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium py-2"
                  onClick={() => setMobileOpen(false)}
                >
                  Войти
                </Link>
                <Link
                  to="/register"
                  className="text-sm font-medium py-2 text-primary"
                  onClick={() => setMobileOpen(false)}
                >
                  Регистрация
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
      {/* Schedule modal */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-sm p-0 gap-0 max-h-[85vh] flex flex-col" aria-describedby={undefined} data-testid="schedule-modal">
          <DialogTitle className="text-base font-semibold flex items-center gap-2 p-4 pb-2 shrink-0">
            <CalendarClock className="h-5 w-5 text-primary" />
            График выдачи
          </DialogTitle>
          <div className="overflow-y-auto overscroll-contain px-4 pb-4 pt-1">
            {scheduleImage && (
              <img
                src={scheduleImage}
                alt="График выдачи"
                className="w-full rounded object-contain max-h-[40vh] mb-3"
              />
            )}
            {scheduleText ? (
              <div
                className="text-sm whitespace-pre-line leading-relaxed"
                dangerouslySetInnerHTML={{ __html: scheduleText }}
              />
            ) : (
              <p className="text-sm text-text-muted">Информация о графике выдачи пока не добавлена.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
